/**
 * PainPointSection - Pain-point narrative section (Narrative Shell).
 *
 * Three columns each present a shallow alternative (a watch giving you
 * 12 km, a video showing an outline, a photo freezing one frame) marked
 * with a red ✕, then a reversal sentence highlights "脚底压力怎么转的"
 * in ice color — the unmet need the product addresses.
 *
 * 痛点叙事区：三栏分别呈现浅层替代方案（手表给 12 公里、视频看轮廓、照片
 * 定格一帧）并打上红色 ✕，随后一句反转句以冰色高亮"脚底压力怎么转的"，
 * 点出产品要解决的未被满足的需求。
 */
import type { CSSProperties } from 'react';

// ----- Styles -----

const sectionStyle: CSSProperties = {
  padding: 'var(--space-2xl) var(--space-xl)',
  background: 'var(--bg-base)',
};

const titleStyle: CSSProperties = {
  margin: 0,
  marginBottom: 'var(--space-xl)',
  fontSize: 'var(--fs-xl)',
  color: 'var(--text-primary)',
  textAlign: 'center',
  fontWeight: 'var(--fw-semibold)',
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 'var(--space-lg)',
};

const cardStyle: CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-lg)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: 'var(--space-md)',
};

const cardTextStyle: CSSProperties = {
  fontSize: 'var(--fs-md)',
  color: 'var(--text-secondary)',
};

const xMarkStyle: CSSProperties = {
  fontSize: 'var(--fs-xl)',
  fontWeight: 'var(--fw-bold)',
  color: 'var(--status-bad)',
  lineHeight: 1,
};

const reversalStyle: CSSProperties = {
  marginTop: 'var(--space-2xl)',
  fontSize: 'var(--fs-xl)',
  color: 'var(--text-primary)',
  textAlign: 'center',
  fontWeight: 'var(--fw-semibold)',
};

const highlightStyle: CSSProperties = {
  color: 'var(--color-ice)',
};

// ----- Column data (inline SVG icons) -----
// 各栏数据（内联 SVG 图标）

interface PainColumn {
  key: string;
  text: string;
  icon: JSX.Element;
}

const COLUMNS: PainColumn[] = [
  {
    key: 'watch',
    text: '给你 12 公里',
    // Circle + clock hands / 表盘 + 时针分针
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
        <circle cx="20" cy="20" r="14" stroke="var(--text-tertiary)" strokeWidth="2" />
        <path
          d="M20 12v8l5 3"
          stroke="var(--text-tertiary)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    key: 'camera',
    text: '看到轮廓',
    // Rectangle body + triangle lens / 矩形机身 + 三角镜头
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
        <rect x="6" y="12" width="22" height="16" rx="2" stroke="var(--text-tertiary)" strokeWidth="2" />
        <path
          d="M28 18l6-3v10l-6-3z"
          stroke="var(--text-tertiary)"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    key: 'frame',
    text: '定格一帧',
    // Rectangle frame + sun + mountain / 相框 + 太阳 + 山
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
        <rect x="6" y="8" width="28" height="24" rx="2" stroke="var(--text-tertiary)" strokeWidth="2" />
        <circle cx="14" cy="16" r="2.5" stroke="var(--text-tertiary)" strokeWidth="1.5" />
        <path
          d="M10 28l7-8 5 5 4-4 6 7z"
          stroke="var(--text-tertiary)"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

// ----- Component -----

export default function PainPointSection() {
  return (
    <section style={sectionStyle}>
      <h2 style={titleStyle}>为什么你需要这个产品</h2>

      {/* Three pain-point columns / 三栏痛点 */}
      <div style={gridStyle}>
        {COLUMNS.map((col) => (
          <div key={col.key} style={cardStyle}>
            {col.icon}
            <span style={cardTextStyle}>{col.text}</span>
            <span style={xMarkStyle}>✕</span>
          </div>
        ))}
      </div>

      {/* Reversal sentence — ice-colored highlight on the unmet need.
          反转句 — 以冰色高亮未被满足的需求。 */}
      <p style={reversalStyle}>
        但<span style={highlightStyle}>脚底压力怎么转的</span>？没人看到过。
      </p>
    </section>
  );
}
